"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InnerCircleForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="mx-auto max-w-xl border border-border bg-card/40 p-8 text-center rounded-md">
        <p className="font-default text-2xl text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 font-mono text-xs uppercase tracking-widest rounded-md">
          Register Another Observer
        </Button>
      </div>;
  }
  const interestOptions = ["Solar Chromosphere", "Deep-Sky & Cosmos", "Technical Logbooks", "Live Streams", "Forum Discussions"];
  return <Form onSubmit={handleSubmit} className="mx-auto w-full max-w-3xl space-y-10">
      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Observer name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Observer Name
              </FormLabel>
              <FormControl>
                <Input placeholder="C. SAGAN" className="h-12 border-x-0 border-t-0 border-b border-border bg-transparent px-0 font-mono text-sm tracking-wider text-foreground placeholder:text-muted-foreground/40 focus-visible:border-accent-foreground focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px] uppercase tracking-widest text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Transmission Endpoint
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="OBSERVER@SOLOBSERVATORY.COM" className="h-12 border-x-0 border-t-0 border-b border-border bg-transparent px-0 font-mono text-sm tracking-wider text-foreground placeholder:text-muted-foreground/40 focus-visible:border-accent-foreground focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px] uppercase tracking-widest text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone_number" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Signal Contact — Optional
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (000) 000-0000" className="h-12 border-x-0 border-t-0 border-b border-border bg-transparent px-0 font-mono text-sm tracking-wider text-foreground placeholder:text-muted-foreground/40 focus-visible:border-accent-foreground focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px] uppercase tracking-widest text-destructive" />
          </FormItem>} />

      <FormField control={control} name="interest_areas" render={({
      field
    }) => {
      const selected: string[] = Array.isArray(field.value) ? field.value : [];
      const toggle = (option: string) => {
        if (selected.includes(option)) {
          field.onChange(selected.filter(v => v !== option));
        } else {
          field.onChange([...selected, option]);
        }
      };
      return <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Observational Focus
              </FormLabel>
              <FormControl>
                <div className="flex flex-wrap gap-3 pt-2">
                  {["Solar Chromosphere", "Deep-Sky & Cosmos", "Technical Logbooks", "Live Streams", "Forum Discussions"].map((option, i) => {
              const isActive = selected.includes(option);
              const isSolar = option.includes("Solar");
              return <Button key={option} data-index={i} type="button" onClick={() => toggle(option)} variant={isActive ? "default" : "outline"} className={`h-10 rounded-md font-mono text-[10px] uppercase tracking-[0.2em] px-4 shadow-sm transition-all duration-300 ${isActive ? isSolar ? "bg-destructive text-white hover:bg-destructive/90" : "bg-secondary text-white hover:bg-secondary/90" : "border-border text-muted-foreground hover:text-foreground hover:border-accent-foreground"}`}>
                        {option}
                      </Button>;
            })}
                </div>
              </FormControl>
              <FormMessage className="font-mono text-[10px] uppercase tracking-widest text-destructive" />
            </FormItem>;
    }} />

      {submitError && <div className="border border-destructive/50 bg-destructive/10 p-4 rounded-md">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">
            {submitError}
          </p>
        </div>}

      <div className="flex flex-col items-center gap-6 pt-4 sm:flex-row sm:justify-center">
        <Button type="submit" disabled={isSubmitting} size="lg" className="h-12 bg-foreground px-8 font-mono text-xs uppercase tracking-[0.25em] text-background hover:bg-foreground/90 hover:scale-[1.02] transition-transform rounded-md shadow-lg">
          {isSubmitting ? "Synchronizing…" : "Join Observation"}
          <ArrowRight className="ml-2 size-4" />
        </Button>
      </div>
    </Form>;
}
export default function JoinTheObservation() {
  const {
    ref: labelRef,
    isVisible: labelVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  const {
    ref: subheadRef,
    isVisible: subheadVisible
  } = useRevealOnScroll<HTMLParagraphElement>();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="795" id="join-the-observation" className="bg-background text-foreground relative overflow-hidden ![padding:25px_24px_10px_24px]">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute left-0 top-1/2 hidden -translate-y-1/2 lg:block opacity-[0.1]">
        <div className="h-80 w-48 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697763/img/solar_prominence_detail.webp)] border-r border-destructive/30" />
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-0 top-1/2 hidden -translate-y-1/2 lg:block opacity-[0.1]">
        <div className="h-80 w-48 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697763/img/deep_sky_nebula_detail.webp)] border-l border-secondary/30" />
      </div>

      <div className="relative mx-auto max-w-4xl text-center">
        {}
        <div ref={labelRef} className={`font-mono text-[11px] uppercase tracking-[0.3em] transition-all duration-700 ${labelVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="text-accent-foreground">&nbsp;BEGIN.Transmission</span>
        </div>

        {}
        <h2 ref={headingRef} className="font-default mt-10 text-5xl font-bold leading-[1.05] text-foreground md:text-7xl lg:text-[88px]" style={{
        letterSpacing: "-0.04em",
        opacity: headingVisible ? 1 : 0,
        transform: headingVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 800ms cubic-bezier(0.16, 1, 0.3, 1) 100ms, transform 800ms cubic-bezier(0.16, 1, 0.3, 1) 100ms"
      }}>
          Join the Observation
        </h2>

        {}
        <p ref={subheadRef} className={`font-default mx-auto mt-8 max-w-2xl text-lg text-muted-foreground md:text-xl transition-all duration-700 delay-200 ${subheadVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>Access raw data .fits files, technical logbook entries, and priority notifications &amp; schedules for live solar and deep-space streams.</p>

        {}
        <div className="mx-auto mt-12 h-px w-16 bg-border" />

        {}
        <div ref={formRef} className={`mt-16 transition-all duration-700 delay-300 ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <FormProvider formKey="join_observation_form_observatory" formId="join_observation_form_observatory" sectionName="join-the-observation" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: "",
          interest_areas: []
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email_address: {
            required: true,
            pattern: "email"
          }
        }} submitText="Join Observation" successMessage="Signal received. Welcome to the observatory. Your first data packet is being prepared." errorMessage="Transmission failed. Please check your signal and try again.">
            <InnerCircleForm />
          </FormProvider>

          {}
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Observational updates ONCE monthly.</p>
        </div>
      </div>
    </section>;
}